import companyInfo from './themes/themes';

const THEME = process.env.REACT_APP_THEME;
const COMPANY = companyInfo[THEME].company;
const ERROR_START = "We couldn't ";
const TRY_AGAIN = ' Please try again.';
const REFRESH_AND_TRY_AGAIN = ' Please refresh the page and try again.';
const CONTACT_TEAM = ` Please contact the ${companyInfo[THEME].company} team for support.`;

function errorTryAgain(error: string) {
  return error + TRY_AGAIN;
}

export default {
  general: {
    acceptTerms: 'Accept terms or link your account before continuing.',
    saveError: ERROR_START + 'save your information.' + REFRESH_AND_TRY_AGAIN,
  },
  lines: {
    success: (name: string, action: string) =>
      `Line ${name} successfully ${action}.`,
    bulkDeleteSuccess: (numLines: number) =>
      `${numLines} ${numLines === 1 ? 'Line' : 'Lines'} successfully deleted.`,
    onboarding: {
      createMoreLines: (linesRemaining: number) =>
        `Create ${linesRemaining} more ${
          linesRemaining === 1 ? 'Line' : 'Lines'
        } to submit your Plan`,
      firstPlanRequirements: `${COMPANY} requires a minimum of 3 lines per product, spanning 6 months or longer, to submit a Growth Plan.`,
      generatedLines: `${COMPANY} Growth Plans help you grow long-term, and one batch just won’t do it - so we’ve generated a year’s worth of supply chain operations which you can edit or submit as is.`,
    },
    maxStages: 'Reached maximum number of stages.',
    minStages: 'Each Line must include at least 2 stages.',
    autoCreate: {
      lineNotFound: 'Line not found.',
      generalError: ERROR_START + 'auto-generate Lines for you.' + TRY_AGAIN,
      generateWithoutLinesError:
        'Must be at least one line in order to auto-generate Lines.',
    },
    invalidLineLength: 'Line must be less than 10 months.',
    lineIsToLong:
      'Optimally planned supply chains tend to range between 3-6 months long in order to avoid surplus. Moreover, companies with Lines up to 6 month long spend 11% less on logistics, on average. We recommend editing your Lines to be between 3-6 months long.',
    lineIsToShort:
      'Optimally planned supply chains tend to range between 3-6 months long, often leading to savings on inventory manufacturing and freight. For this reason, Lines between 3-6 months long are 41% more likely to be accepted by 8fig’s planning team. We recommend editing this Line to be between 3-6 months long.',
    lineIsHeavy:
      'Optimally planned supply chains tend to include costs both before and after the stock is on hand. Lines with a more balanced cost distribution are 26% more likely to be accepted by 8fig’s planning team. We recommend editing this Line to include costs both before and after the stock is on hand.',
    editLineError: ERROR_START + 'edit the Line.' + TRY_AGAIN,
    createLineError: ERROR_START + 'create the Line.' + TRY_AGAIN,
    changeRequestEditError: ERROR_START + 'edit the existing Line.' + TRY_AGAIN,
    changeRequestSubmitError:
      ERROR_START + 'submit your change request.' + REFRESH_AND_TRY_AGAIN,
    changeRequestLineDeleted:
      'This Line was already marked as canceled. Our team will respond to your request soon.',
    changeRequestLineInProgress:
      'You cannot cancel a Line that is already in progress.',
    editLineSuccess: (name: string) => `${name} successfully edited`,
    createLineSuccess: 'Successfully created new Line.',
    changeActiveLineRequestSuccess:
      'Successfully created change request. Our team will respond to your request soon.',
    pendingChangeApproval:
      'Your change request is pending your approval. Please see the Dashboard for more information.',
    deleteLineError: errorTryAgain('delete the Line.'),
    bulkDeleteError: errorTryAgain('delete the Lines.'),
    uploadDocumentError: errorTryAgain('upload the document.'),
    lineEditErrors: {
      nameRequired: 'Line name is required.',
      inventoryRequired: 'Line inventory is required.',
      inventoryValidNum: 'Line inventory must be a valid number.',
      inventoryPositiveNum: 'Line inventory must be greater than 0.',
      stageAmountRequired: 'Stage amount is required.',
      stageAmountValidNum: 'Enter a number between 1,000 and 1,000,000.',
      stageDateFutureDate: 'Stage date cannot be in the past.',
      stageDateValidDate: 'Enter a valid  date.',
      nameNotUnique: 'Line name must be unique.',
    },
    noLinesMatchingFilter: 'No Lines match the selected filter.',
  },
  plans: {
    deleteSuccess: 'Plan successfully deleted.',
    acceptPlanChanges: 'Your Growth Plan changes have been applied.',
    rejectPlanChanges: 'You successfully rejected the growth plan changes.',
    proposeAlternativeChange:
      'Our team will review your feedback and let you know if we can provide an updated Growth Plan',
    errors: {
      nameUpdate: (name: string) => {
        return ERROR_START + `rename your Plan to ${name}.` + TRY_AGAIN;
      },
      delete: ERROR_START + 'delete your Plan.' + TRY_AGAIN,
      openContract: ERROR_START + 'open your agreement.' + TRY_AGAIN,
      missing: 'Some Plan details are missing.' + REFRESH_AND_TRY_AGAIN,
      acceptPlanChanges:
        ERROR_START + 'activate your Growth Plan.' + REFRESH_AND_TRY_AGAIN,
      proposeAlternativeChange:
        ERROR_START + 'send your feedback successfully.' + TRY_AGAIN,
    },
  },
  product: {
    errors: {
      notFoundAsin: ERROR_START + 'find a product with that ASIN.' + TRY_AGAIN,
      notFoundUpdate:
        ERROR_START +
        "find the product you're trying to update." +
        REFRESH_AND_TRY_AGAIN,
    },
  },
  submitApplication: {
    errors: {
      planTooShort: 'Insufficient Planning Horizon',
      noAmazonAccount:
        "It looks like you don't have an Amazon Seller account connected. You can also use the Continue without Verification option.",
      noBankAccount:
        "It looks like you don't have a bank account connected. You can also use the Continue without Verification option.",
      submit:
        'We encountered a problem creating your financing application. Please try again.',
      linesLength:
        'To ensure the accuracy of our sales and cash flow forecasting, we ask that all Growth Plan applications include a minimum of 6 months of planned operations. Unsure of your plans? Enter your best estimate, and remember that 8fig plans are flexible. You will be able to edit and pivot as more details become available.',
      productNumber:
        'Please include 3 Lines or more for each Product. This ensures our forecasting algorithm works optimally.',
    },
    tooltips: {
      notSupportedStore: `You cannot submit your Lines because ${COMPANY} is unable to offer a Growth Plan for the e-commerce platform you selected. If you have questions, please contact us.`,
    },
  },
  bank: {
    addSuccess: 'Bank account successfully added.',
    updateSuccess: 'Bank account successfully updated.',
    errors: {
      misssingLinkToken:
        ERROR_START +
        'connect to Plaid, the secure service for selecting your bank.' +
        REFRESH_AND_TRY_AGAIN,
      addBank: ERROR_START + 'add your bank account.' + TRY_AGAIN,
      updateBank:
        ERROR_START + 'update your bank account.' + REFRESH_AND_TRY_AGAIN,
      notFound: ERROR_START + 'find your bank account.' + TRY_AGAIN,
      oAuth: ERROR_START + 'connect to your bank account.' + TRY_AGAIN,
      plaidLinkToken:
        ERROR_START +
        'load the token for connecting your bank account.' +
        TRY_AGAIN,
      wiseRedirect:
        ERROR_START +
        'redirect you to the Wise website.' +
        REFRESH_AND_TRY_AGAIN,
      deletionFailure: `${ERROR_START}delete the required bank account.${TRY_AGAIN}`,
    },
  },
  wiseRedirect: {
    success: 'Bank was successfully connected.',
    errors: { update: ERROR_START + 'save your bank information.' + TRY_AGAIN },
  },
  onboarding: {
    sellerName: {
      saveErr: ERROR_START + 'save the seller name.' + TRY_AGAIN,
    },
    errors: {
      mobileSubmitApp:
        ERROR_START + 'submit your application.' + REFRESH_AND_TRY_AGAIN,
    },
  },
  auth: {
    resentVerifyEmailCode: 'New verification code sent.',
    errors: {
      signout: ERROR_START + 'sign you out.' + REFRESH_AND_TRY_AGAIN,
      googleLoginIn: ERROR_START + 'open Google.' + REFRESH_AND_TRY_AGAIN,
      addSignUpData:
        ERROR_START + 'save some important user information.' + CONTACT_TEAM,
    },
  },
};
